import React from "react";
import { toast } from "sonner";
import { Twitter, Facebook, Linkedin, Link as LinkIcon, Mail } from "lucide-react";

/**
 * ShareBar — social share buttons that generate backlinks when users share.
 * Renders as a minimal inline row.
 *
 * Props:
 *  - url: absolute URL to share (defaults to current page)
 *  - title: title/text
 *  - variant: 'light' | 'dark' (default 'dark')
 */
const ShareBar = ({ url, title = "POG Spirits — Icons evolve. So we did.", variant = "dark", testid = "share-bar" }) => {
  const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "https://pogspirits.com/");
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedText = encodeURIComponent(title);

  const targets = [
    {
      id: "x",
      label: "Share on X",
      Icon: Twitter,
      href: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}&via=pogspirits`,
    },
    {
      id: "facebook",
      label: "Share on Facebook",
      Icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    },
    {
      id: "linkedin",
      label: "Share on LinkedIn",
      Icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    },
    {
      id: "email",
      label: "Share by Email",
      Icon: Mail,
      href: `mailto:?subject=${encodedText}&body=${encodedUrl}`,
    },
  ];

  const copy = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success("Link copied to clipboard.");
  };

  const isLight = variant === "light";

  return (
    <div
      className={`inline-flex items-center gap-2 ${isLight ? "text-[#14100D]" : "text-[#EFE7D6]"}`}
      data-testid={testid}
    >
      <span
        className={`overline text-[10px] mr-2 ${
          isLight ? "!text-[#14100D]/60" : "!text-[#EFE7D6]/50"
        }`}
      >
        Share
      </span>
      {targets.map((t) => (
        <a
          key={t.id}
          href={t.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t.label}
          data-testid={`share-${t.id}`}
          className={`w-9 h-9 flex items-center justify-center border transition-colors ${
            isLight
              ? "border-[#14100D]/25 hover:border-[#14100D] hover:bg-[#14100D] hover:text-[#EFE7D6]"
              : "border-[#EFE7D6]/25 hover:border-[#E85A15] hover:text-[#E85A15]"
          }`}
        >
          <t.Icon size={14} strokeWidth={1.5} />
        </a>
      ))}
      <button
        type="button"
        onClick={copy}
        aria-label="Copy link"
        data-testid="share-copy"
        className={`w-9 h-9 flex items-center justify-center border transition-colors ${
          isLight
            ? "border-[#14100D]/25 hover:border-[#14100D] hover:bg-[#14100D] hover:text-[#EFE7D6]"
            : "border-[#EFE7D6]/25 hover:border-[#E85A15] hover:text-[#E85A15]"
        }`}
      >
        <LinkIcon size={14} strokeWidth={1.5} />
      </button>
    </div>
  );
};

export default ShareBar;
